"use client";
import { useState, useEffect } from "react";
import { Filter, Search } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import PersonalView from "./PersonalView";
import PersonalChatView from "./PersonalChatView";
import CommunityMain from "./CommunityMain";
import CommunityView from "./CommunityView";
import HalperChatView from "./HalperChatView";
import instagram_analytics from "../../assets/instagram-analytics.png";
import telegram_analytics from "../../assets/telegram-analytics.png";
import facebook_analytics from "../../assets/facebook-analytics.png";
import whatsapp_analytics from "../../assets/whatsapp-analytics.png";
import mail_analytics from "../../assets/mail-analytics.png";
import { HiChevronUpDown } from "react-icons/hi2";
import { REST_API_URL } from "../../api/config";
import {
  getHalperChatUnreadCount,
  postHalperMarkAsRead,
} from "../../api/chat/chatServices";
const API_BASE = REST_API_URL;

const getUserTimeZone = () => {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

const buildHeaders = (token, additionalHeaders = {}) => {
  const headers = {
    "Content-Type": "application/json",
    ...additionalHeaders,
  };

  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }

  const timezone = getUserTimeZone();
  if (timezone) {
    headers["User-Timezone"] = timezone;
  }

  return headers;
};

const platformIcons = {
  instagram: instagram_analytics,
  whatsapp: whatsapp_analytics,
  facebook: facebook_analytics,
  telegram: telegram_analytics,
  gmail: mail_analytics,
};

const getChatDisplayName = (chat) => {
  if (!chat) return "Unknown user";

  if (chat.platform === "telegram") {
    return (
      chat.full_name ||
      chat.tg_profile_name ||
      (chat.telegram_username ? `@${chat.telegram_username}` : null) ||
      (chat.tg_username ? `@${chat.tg_username}` : null) ||
      (chat.telegram_id ? `${chat.telegram_id}` : null) ||
      (chat.tg_phone ? `+${chat.tg_phone}` : null) ||
      "Unknown user"
    );
  }

  if (chat.platform === "whatsapp") {
    return chat.wa_profile_name || (chat.wa_user_id ? `+${chat.wa_user_id}` : "Unknown user");
  }

  return (
    chat.full_name ||
    (chat.instagram_username ? `@${chat.instagram_username}` : null) ||
    "Unknown user"
  );
};

const getOnOffEndpoint = (platform, chatId) => {
  const endpoints = {
    instagram: `${API_BASE}/messaging/instagram-on-off/${chatId}/`,
    whatsapp: `${API_BASE}/messaging/whatsapp-on-off/${chatId}/`,
    telegram: `${API_BASE}/messaging/telegram-on-off/${chatId}/`,
  };

  return endpoints[platform] ?? endpoints.instagram;
};

export default function Chat({ defaultHalper = false }) {
  useEffect(() => {
    if (defaultHalper) {
      setActiveTab("Personal");
      setSelectedChat("Halper");
    }
  }, [defaultHalper]);
  const token =
    localStorage.getItem("accessToken") ||
    sessionStorage.getItem("accessToken");

  const [activeTab, setActiveTab] = useState("Personal");
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCommunity, setSelectedCommunity] = useState(null);
  const [communities, setCommunities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [filterPlatform, setFilterPlatform] = useState([]);
  const [halperChatUnreadCount, setHalperChatUnreadCount] = useState(0);
  const headers = buildHeaders(token);

  const togglePlatform = (key) => {
    if (key === "all") return setFilterPlatform([]);
    setFilterPlatform((prev) =>
      prev.includes(key) ? prev.filter((p) => p !== key) : [...prev, key],
    );
  };

  useEffect(() => {
    if (activeTab === "Community") loadCommunities();
  }, [activeTab]);

  useEffect(() => {
    getHalperChatUnreadCount()
      .then((data) => setHalperChatUnreadCount(data.unread_count))
      .catch(console.error);
  }, []);

  const loadCommunities = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/community/joined-communities/`, {
        headers,
      });
      const data = await res.json();

      setCommunities(Array.isArray(data) ? data : data.results || []);
    } catch (e) {
      console.error("❌ Error fetching communities:", e);
    } finally {
      setLoading(false);
    }
  };

  const loadConversations = async () => {
    try {
      setLoading(true);

      const res = await fetch(`${API_BASE}/messaging/conversations/`, {
        headers,
      });
      const data = await res.json();
      setConversations(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("❌ Error fetching conversations:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "Personal") loadConversations();
  }, [activeTab]);

  const toggleOnOff = async (chat) => {
    try {
      const endpoint = getOnOffEndpoint(chat.platform, chat.id);

      const res = await fetch(endpoint, {
        method: "GET",
        headers,
      });

      const data = await res.json();
      setConversations((prev) =>
        prev.map((c) =>
          c.id === chat.id ? { ...c, on_off: data.on_off ?? !chat.on_off } : c,
        ),
      );
    } catch (err) {
      console.error("❌ Error toggling on_off:", err);
    }
  };

  const filteredCommunities = communities.filter(
    (community) =>
      community.name?.toLowerCase().includes(search.toLowerCase()) ||
      community.description?.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="flex gap-5 w-full h-[99%] bg-gray-50 p-2 rounded-2xl">
      <motion.div
        className="w-[400px] bg-white rounded-2xl flex flex-col p-4 gap-5 shadow-sm relative"
        initial={{ x: -30, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
      >
        <div className="relative">
          <input
            type="text"
            placeholder={
              activeTab === "Personal"
                ? "Search personal chats..."
                : "Search communities..."
            }
            className="w-full h-[48px] pl-10 pr-4 rounded-2xl bg-[#F6F6F6] text-gray-600 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5563F6]"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Search className="absolute left-3 top-3 text-gray-400" size={20} />
        </div>

        <div className="flex items-center gap-[10px] w-[342px] h-[60px]">
          <div className="flex items-center justify-between w-[232px] h-[60px] border border-[rgba(154,153,171,0.2)] rounded-full px-[10px] py-[8px]">
            {["Personal", "Community"].map((tab) => (
              <div
                key={tab}
                onClick={() => {
                  setActiveTab(tab);
                  setSelectedChat(null);
                  setSelectedCommunity(null);
                }}
                className={`flex justify-center items-center w-[100px] h-[44px] rounded-full cursor-pointer ${
                  activeTab === tab ? "bg-[#5563F6]" : ""
                }`}
              >
                <span
                  className={`text-[14px] ${
                    activeTab === tab ? "text-white" : "text-[#9A99AB]"
                  }`}
                >
                  {tab}
                </span>
              </div>
            ))}
          </div>

          <button
            className="flex items-center gap-1 text-gray-500 text-sm relative"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} /> Filters
          </button>

          <AnimatePresence>
            {showFilters && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="absolute left-[142px] top-[141px] z-[6] w-[230px] bg-white border border-[rgba(154,153,171,0.2)] shadow-md rounded-[16px] p-4 flex flex-col gap-10"
              >
                {[
                  { key: "all", label: "All platforms", icon: null },
                  {
                    key: "instagram",
                    label: "Instagram",
                    icon: instagram_analytics,
                  },
                  {
                    key: "whatsapp",
                    label: "Whatsapp",
                    icon: whatsapp_analytics,
                  },
                  {
                    key: "facebook",
                    label: "Facebook",
                    icon: facebook_analytics,
                  },
                  {
                    key: "telegram",
                    label: "Telegram",
                    icon: telegram_analytics,
                  },
                  { key: "gmail", label: "Mail", icon: mail_analytics },
                ].map((item) => (
                  <div
                    key={item.key}
                    className="flex items-center justify-between w-full cursor-pointer"
                    onClick={() => togglePlatform(item.key)}
                  >
                    <div className="flex items-center gap-3">
                      {item.icon ? (
                        <img
                          src={item.icon}
                          className="w-6 h-6"
                          alt={item.label}
                        />
                      ) : (
                        <HiChevronUpDown style={{ fontSize: "22px" }} />
                      )}
                      <span className="text-[#302B2B] text-[16px]">
                        {item.label}
                      </span>
                    </div>

                    <div
                      className={`w-5 h-5 rounded-md flex items-center justify-center border
                        ${
                          filterPlatform.includes(item.key) ||
                          (item.key === "all" && filterPlatform.length === 0)
                            ? "bg-[#5563F6] border-[#5563F6]"
                            : "border-[#C7C6D3]"
                        }`}
                    >
                      {(filterPlatform.includes(item.key) ||
                        (item.key === "all" &&
                          filterPlatform.length === 0)) && (
                        <svg
                          width="10"
                          height="8"
                          viewBox="0 0 10 8"
                          fill="none"
                        >
                          <path
                            d="M1.5 4L4 6.5L8.5 1"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {activeTab === "Personal" && (
          <div className="flex-1 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#302B2B]">Recent Chats</h3>
              <button
                onClick={loadConversations}
                className="text-sm text-[#5563F6] hover:underline"
              ></button>
            </div>

            <div
              onClick={() => {
                postHalperMarkAsRead().then(() => setHalperChatUnreadCount(0));
                setSelectedChat("Halper");
              }}
              className="flex items-center justify-between w-[full] h-[76px] bg-[#F6F6F6] rounded-[10px] px-[10px] cursor-pointer hover:bg-gray-200 transition mb-3"
            >
              <div className="flex items-center gap-[10px]">
                <div className="relative w-[50px] h-[50px]">
                  <div className="absolute inset-0 bg-[#D9D9D9] rounded-full" />
                  <span className="absolute inset-0 flex items-center justify-center text-[22px] font-bold text-[#5563F6]">
                    H
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="font-[Helvetica] text-[18px] text-[#302B2B]">
                    Halper
                  </span>
                  <span className="font-[Helvetica] text-[14px] text-[#9A99AB]">
                    What's up? Have you seen any...
                  </span>
                </div>
              </div>
              {halperChatUnreadCount > 99 && (
                <span
                  className="  inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-semibold  bg-[#5563F6] rounded-full  text-white "
                  aria-label={`Unread messages: ${halperChatUnreadCount}`}
                >
                  {halperChatUnreadCount > 99 ? "99+" : halperChatUnreadCount}
                </span>
              )}
            </div>

            {loading ? (
              <div className="flex justify-center items-center h-20">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#5563F6]"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {conversations
                  .filter((chat) =>
                    filterPlatform.length === 0
                      ? true
                      : filterPlatform.includes(chat.platform),
                  )
                  .map((chat) => (
                    <motion.div
                      key={chat.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`p-3 rounded-xl cursor-pointer transition-all border-2 ${
                        selectedChat?.id === chat.id
                          ? "border-[#5563F6] bg-blue-50"
                          : "border-transparent bg-gray-50 hover:bg-gray-100"
                      }`}
                      onClick={() => setSelectedChat(chat)}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className="relative">
                            <img
                              src={
                                chat.profile_picture ||
                                chat.image_url ||
                                "https://cdn-icons-png.flaticon.com/512/149/149071.png"
                              }
                              alt={getChatDisplayName(chat)}
                              className="w-12 h-12 rounded-full object-cover border border-gray-200"
                            />

                            <img
                              src={platformIcons[chat.platform?.toLowerCase()]}
                              className="w-5 h-5 absolute bottom-0 right-0 rounded-full border border-white"
                              alt={chat.platform}
                            />
                          </div>

                          <div className="flex-1 min-w-0">
                            <h4 className="font-medium text-[#302B2B] text-sm truncate">
                              {getChatDisplayName(chat)}
                            </h4>
                            <p className="text-xs text-[#9A99AB] truncate">
                              {chat.last_message || "No messages"}
                            </p>
                          </div>
                        </div>

                        <div className="flex flex-col items-end gap-1">
                          <span className="text-xs text-gray-400 whitespace-nowrap">
                            {new Date(
                              chat.last_message_time,
                            ).toLocaleTimeString([], {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleOnOff(chat);
                            }}
                            className={`relative w-10 h-5 rounded-full transition-colors ${
                              chat.on_off ? "bg-[#5563F6]" : "bg-gray-300"
                            }`}
                          >
                            <div
                              className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transform transition-transform ${
                                chat.on_off ? "translate-x-5" : "translate-x-0"
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    </motion.div>
                  ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "Community" && (
          <div className="flex-1 overflow-y-auto">
            <h3 className="font-semibold text-[#302B2B] mb-3">
              Your Communities
            </h3>

            {loading ? (
              <div className="flex justify-center py-5">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#5563F6]"></div>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredCommunities.map((community) => (
                  <motion.div
                    key={community.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-3 rounded-xl cursor-pointer transition-all border border-[#E5E7EB] hover:bg-gray-50"
                    onClick={() => setSelectedCommunity(community)}
                  >
                    <div className="flex items-start gap-3">
                      <img
                        src={community.icon}
                        className="w-12 h-12 rounded-full object-cover"
                      />

                      <div className="flex-1">
                        <div className="flex items-center gap-1">
                          <span className="font-semibold text-[16px] text-[#302B2B]">
                            {community.name}
                          </span>
                        </div>

                        <div className="flex items-center gap-1 text-[13px] text-[#4CAF50]">
                          <span className="text-[10px]">●</span>
                          {community.joined_count?.toLocaleString()} Members
                        </div>

                        <p className="text-xs text-[#9A99AB] truncate max-w-[230px]">
                          {community.description}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        )}
      </motion.div>

      <motion.div
        className="flex flex-col flex-1 bg-white rounded-2xl shadow-sm p-2"
        initial={{ y: 20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
      >
        {activeTab === "Personal" && !selectedChat && <PersonalView />}
        {activeTab === "Personal" && selectedChat === "Halper" && (
          <HalperChatView />
        )}
        {activeTab === "Personal" &&
          selectedChat &&
          selectedChat !== "Halper" && (
            <PersonalChatView
              chat={selectedChat}
              platformIcons={platformIcons}
            />
          )}

        {activeTab === "Community" && !selectedCommunity && (
          <CommunityMain
            onSelect={setSelectedCommunity}
            onJoined={loadCommunities}
          />
        )}
        {activeTab === "Community" && selectedCommunity && (
          <CommunityView
            community={selectedCommunity}
            onBack={() => setSelectedCommunity(null)}
          />
        )}
      </motion.div>
    </div>
  );
}
